import { useState, useEffect, useRef, useCallback } from "react";
import { CourseStageUIType } from "../types/promotionResponse";
import { useStudent } from "../../../peoples/students/hooks/useStudent";

// Storage key for filter state
const FILTER_STATE_KEY = "student_promotion_filter_state";

export type PromotionFilterState = {
  selectedSession: string;
  selectedCourse: string;
  selectedFromStage: string;
  selectedToStage: string;
  pageNumber: number;
  recordsPerPage: number;
};

/** Number of records fetched per lazy chunk */
const LAZY_PAGE_SIZE = 100;

export const usePromotionFilters = () => {
  // Get stored filter state from session storage
  const getStoredFilterState = (): PromotionFilterState | null => {
    const storedState = sessionStorage.getItem(FILTER_STATE_KEY);
    if (storedState) {
      try {
        return JSON.parse(storedState);
      } catch (e) {
        console.error("Error parsing stored filter state:", e);
      }
    }
    return null;
  };

  const storedState = getStoredFilterState();

  // State for filters
  const [selectedSession, setSelectedSession] = useState<string>(
    storedState?.selectedSession || ""
  );

  const [selectedCourse, setSelectedCourse] = useState<string>(
    storedState?.selectedCourse || ""
  );

  const [selectedFromStage, setSelectedFromStage] = useState<string>(
    storedState?.selectedFromStage || ""
  );

  const [selectedToStage, setSelectedToStage] = useState<string>(
    storedState?.selectedToStage || ""
  );

  const [recordsPerPage, setRecordsPerPage] = useState<number>(
    storedState?.recordsPerPage || 10
  );

  const [pageNumber, setPageNumber] = useState<number>(
    storedState?.pageNumber || 1
  );

  const [search, setSearch] = useState<string>("");

  // Course stages state
  const [courseStages, setCourseStages] = useState<CourseStageUIType[]>([]);

  // ── Lazy-load ("All") mode ────────────────────────────────────────────────
  const [isAllMode, setIsAllMode] = useState(false);
  const [lazyPage, setLazyPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<any[]>([]);
  const [lazyHasMore, setLazyHasMore] = useState(true);

  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);

  // RTK Query
  const { getStudentState, paginationData } = useStudent({
    page: isAllMode ? lazyPage : pageNumber,
    size: isAllMode ? LAZY_PAGE_SIZE : recordsPerPage,
    search: search,
    course_id: selectedCourse ? parseInt(selectedCourse) : undefined,
    stage_id: selectedFromStage ? parseInt(selectedFromStage) : undefined,
    session_id: selectedSession ? parseInt(selectedSession) : undefined,
    status: "active",
    marks: true,
  });

  const { students: currentPageStudents, isFetching: isFetchingStudents, isLoading: isLoadingStudents, error } = getStudentState();

  const resetLazyState = useCallback(() => {
    setAccumulatedStudents([]);
    setLazyPage(1);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
    setLazyHasMore(true);
  }, []);

  // Store filter state in session storage whenever it changes
  useEffect(() => {
    const filterState = {
      selectedSession,
      selectedCourse,
      selectedFromStage,
      selectedToStage,
      recordsPerPage,
      pageNumber,
    };
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  }, [
    selectedSession,
    selectedCourse,
    selectedFromStage,
    selectedToStage,
    recordsPerPage,
    pageNumber,
  ]);

  // Accumulation effect
  useEffect(() => {
    if (currentPageStudents && currentPageStudents.length >= 0) {
      if (isAllMode) {
        if (isFetchingStudents) return;
        if (lazyPage === lastProcessedPage.current) return;

        // No data -> end of results
        if (currentPageStudents.length === 0) {
          setLazyHasMore(false);
          pageRequestedRef.current = false;
          return;
        }

        setLazyHasMore(currentPageStudents.length === LAZY_PAGE_SIZE);
        lastProcessedPage.current = lazyPage;
        pageRequestedRef.current = false;

        setAccumulatedStudents((prev) => {
          if (lazyPage === 1) return currentPageStudents;
          const newUnique = currentPageStudents.filter(
            (newS: any) => !prev.some((p: any) => p.id === newS.id)
          );
          return [...prev, ...newUnique];
        });
        
        const total = paginationData.totalItems || 0;
        const fetchedSoFar = lazyPage * LAZY_PAGE_SIZE;
        if (currentPageStudents.length < LAZY_PAGE_SIZE || fetchedSoFar >= total) {
          setLazyHasMore(false);
        }
      }
    }
  }, [currentPageStudents, isAllMode, lazyPage, isFetchingStudents, paginationData.totalItems]);

  const loadNextLazyPage = useCallback(() => {
    if (!isAllMode || !lazyHasMore) return;
    if (pageRequestedRef.current) return;
    pageRequestedRef.current = true;
    setLazyPage(prev => prev + 1);
  }, [isAllMode, lazyHasMore]);

  // Handler functions for filter changes
  const handleSessionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSession(e.target.value);
    setSelectedCourse("");
    setSelectedFromStage("");
    setSelectedToStage("");
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handleCourseChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    courses: any[],
    shouldResetStages: boolean = true
  ) => {
    const courseId = e.target.value;
    setSelectedCourse(courseId);

    // Update course stages
    if (courseId) {
      const selectedCourseObj = courses.find(
        (course) => course.id.toString() === courseId
      );
      if (selectedCourseObj?.courseStages) {
        setCourseStages(
          selectedCourseObj.courseStages.map((stage: any, index: number) => ({
            id: stage.id,
            name: stage.name,
            // Use index + 1 as a proxy for level
            level: index + 1,
          }))
        );
      }
    } else {
      setCourseStages([]);
    }

    if (shouldResetStages) {
      setSelectedFromStage("");
      setSelectedToStage("");
      setPageNumber(1);
      if (isAllMode) resetLazyState();
    }
  };

  const handleFromStageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedStageId = e.target.value;
    setSelectedFromStage(selectedStageId);

    // Auto-select the next stage if possible
    if (selectedStageId && courseStages.length > 0) {
      const currentStage = courseStages.find(
        (stage) => stage.id.toString() === selectedStageId
      );

      if (currentStage) {
        // Find the next stage based on level
        const nextStage = courseStages.find(
          (stage) => stage.level === currentStage.level + 1
        );

        if (nextStage) {
          setSelectedToStage(nextStage.id.toString());
        } else {
          // If there's no next stage, reset the to stage selection
          setSelectedToStage("");
        }
      }
    } else {
      // If no from stage is selected, reset to stage
      setSelectedToStage("");
    }

    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handleToStageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedToStage(e.target.value);
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  // Handle pagination
  const handlePageChange = (page: number, pageSize?: number) => {
    if (pageSize !== undefined) {
      const total = paginationData.totalItems || 0;
      const isSelectingAll = total > 0 && pageSize >= total;

      if (isSelectingAll) {
        setIsAllMode(true);
        resetLazyState();
        setRecordsPerPage(pageSize);
        return;
      }

      if (isAllMode) {
        setIsAllMode(false);
        resetLazyState();
      }
      setRecordsPerPage(pageSize);
    }
    setPageNumber(page);
  };

  // Get stage options based on selected course
  const getStageOptions = () => {
    return courseStages || [];
  };

  // Get "To Stage" options (only stages after the selected "From Stage")
  const getToStageOptions = () => {
    if (!selectedFromStage || !courseStages.length) return [];

    const currentStage = courseStages.find(
      (stage) => stage.id.toString() === selectedFromStage
    );

    if (!currentStage) return [];

    return courseStages
      .filter((stage) => stage.level > currentStage.level)
      .map((stage) => ({
        id: stage.id,
        name: stage.name,
      }));
  };

  // Check if all filters are selected
  const areAllFiltersSelected = () =>
    selectedSession && selectedCourse && selectedFromStage;

  // Check if all filters including 'to stage' are selected
  const areAllFiltersWithToStageSelected = () =>
    areAllFiltersSelected() && selectedToStage;

  // Get missing filters message
  const getMissingFiltersMessage = () => {
    const missingFilters = [];
    if (!selectedSession) missingFilters.push("Session");
    if (!selectedCourse) missingFilters.push("Course");
    if (!selectedFromStage) missingFilters.push("From Stage");

    let message = missingFilters.join(", ");
    if (selectedFromStage && !selectedToStage) {
      message += message ? ", To Stage" : "To Stage";
    }

    return message;
  };

  // Check if the selected stage is the last stage in the course
  const isLastStage = () => {
    if (!selectedFromStage || courseStages.length === 0) return false;

    // Find the current stage
    const currentStage = courseStages.find(
      (stage) => stage.id.toString() === selectedFromStage
    );

    if (!currentStage) return false;

    // Check if there are no stages with higher levels
    const higherStages = courseStages.filter(
      (stage) => stage.level > currentStage.level
    );

    return higherStages.length === 0;
  };

  return {
    // Filter state
    selectedSession,
    selectedCourse,
    selectedFromStage,
    selectedToStage,
    recordsPerPage,
    pageNumber,
    search,
    courseStages,

    // Lazy load states
    isAllMode,
    accumulatedStudents,
    lazyHasMore,
    currentPageStudents,
    isLoadingStudents,
    isFetchingStudents,
    error,
    paginationData,
    pageRequestedRef,
    loadNextLazyPage,

    // State setters
    setSelectedSession,
    setSelectedCourse,
    setSelectedFromStage,
    setSelectedToStage,
    setRecordsPerPage,
    setPageNumber,
    setSearch,

    // Handlers
    handleSessionChange,
    handleCourseChange,
    handleFromStageChange,
    handleToStageChange,
    handleSearchChange,
    handlePageChange,

    // Helper functions
    getStageOptions,
    getToStageOptions,
    areAllFiltersSelected,
    areAllFiltersWithToStageSelected,
    getMissingFiltersMessage,
    isLastStage,
  };
};
